import {
  Button,
  Card,
  CardContent,
  CardHeader,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Typography,
} from "@altimateai/lego";
import { useTeammateByIdWithStats } from "../mcp/useTeammates";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DatamateNotFound from "./DatamateNotFound";
import { Stack } from "@altimateai/lego";
import Integration from "../mcp/Integration";
import DeleteTeammate from "./DeleteTeammate";
import {
  getAssistAggregations,
  getGuardrailAggregations,
  getKnowledgeBaseSources,
} from "@/helpers/apis";

import { useQuery } from "@tanstack/react-query";
import { RelativeComponentLoader } from "@/components/loader";
import { BackButton } from "@/components/button";
import { MoreIcon } from "@/assets/icons";
import dayjs from "dayjs";
import UseDatamateWithComponent from "./UseDatamateWithComponent";
import PrivacyAndCreatorInfo from "@/components/PrivacyAndCreatorInfo";
import { Chart, ChartTypes } from "@/uiCore";
import React from "react";
import { Bar } from "recharts";
import DatamateHistory from "./DatamateHistory";
import { DateRange } from "@/components/dateRange";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { PremiumBanner } from "@/pages/teammate/dataMates/PremiumBanner";
import { getDataMateIcon } from "@/pages/teammate/dataMates/DataMatesList";
import MCPProvider from "../mcp/MCPProvider";
import { MaskingType } from "../security/types";
import useIntegrations from "./useIntegrations";
import { useCanEditPrivacyAware } from "@/helpers/privacyPermissions";
import KnowledgeCatalogCard from "../knowledgeCatalog/KnowledgeCatalogCard";
import { useTenantInfo } from "@/helpers/hooks";
import { Badge } from "@altimateai/lego";
import { Privacy } from "@/helpers/constants";
import { useBreadcrumb, getEntityName } from "@/components/breadcrumbs";

const GUARDRAIL_ACTIONS = [
  { key: MaskingType.MASK, label: "Detect and Mask", color: "#4F46E5" }, // purple
  { key: MaskingType.BLOCK, label: "Block", color: "#5FD0B4" }, // green
  { key: MaskingType.ALLOW, label: "Allow", color: "#2563EB" }, // blue
  { key: "unknown", label: "Unknown", color: "#FBBF24" }, // yellow
];

type GuardrailAggregationsApi = Array<{
  integration_type: string;
  guard_rails: {
    [type: string]: {
      [action: string]: number;
    };
  };
}>;

type GuardrailChartRow = {
  type: string;
  [key: string]: string | number;
};

function transformGuardrailAggregations(raw: unknown): GuardrailChartRow[] {
  if (
    !Array.isArray(raw) ||
    !raw.every(item => typeof item === "object" && item !== null && "guard_rails" in item)
  ) {
    return [];
  }
  const typedRaw = raw as GuardrailAggregationsApi;
  // Collect all types
  const typeSet = new Set<string>();
  typedRaw.forEach(item => {
    Object.keys(item.guard_rails).forEach(type => typeSet.add(type));
  });

  // For each type, sum up actions across all integrations
  const result: GuardrailChartRow[] = Array.from(typeSet).map(type => {
    const entry: GuardrailChartRow = { type };
    GUARDRAIL_ACTIONS.forEach(({ label }) => {
      entry[label] = 0;
    });
    typedRaw.forEach(item => {
      const actions = item.guard_rails[type];
      if (actions) {
        Object.entries(actions).forEach(([action, count]) => {
          const actionObj = GUARDRAIL_ACTIONS.find(a => a.key === action);
          if (actionObj) {
            entry[actionObj.label] = (entry[actionObj.label] as number) + count;
          } else if (action === "Unknown") {
            entry["Unknown"] = (entry["Unknown"] as number) + count; // treat Unknown as Allow
          }
        });
      }
    });
    // remove fields which have value 0
    Object.keys(entry).forEach(key => {
      if (entry[key] === 0) {
        delete entry[key];
      }
    });
    return entry;
  });

  return result;
}

const CustomTeammate = () => {
  const { teammateId } = useParams();
  const { state } = useLocation();
  const navigateTo = useNavigate();
  const [assistsAggregateBy, setAssistsAggregateBy] = React.useState("integrations");
  const [guardrailsAggregateBy, setGuardrailsAggregateBy] = React.useState("integrations");

  const { startDate, endDate, onUpdate } = useDateFromSearchParams(
    dayjs().subtract(28, "day").toDate(),
    dayjs().toDate()
  );

  const { data: assistAggregations, isLoading: isAssistAggregationLoading } = useQuery({
    queryKey: ["assist_aggregations", assistsAggregateBy, startDate, endDate],
    queryFn: () => getAssistAggregations(assistsAggregateBy, startDate, endDate, teammateId),
  });

  const { data: guardRailAggregations, isLoading: isGuardRailAggregationLoading } = useQuery({
    queryKey: ["guard_rails_aggregations", guardrailsAggregateBy, startDate, endDate],
    queryFn: () => getGuardrailAggregations(guardrailsAggregateBy, startDate, endDate, teammateId),
  });
  const { data: dataMate, isLoading } = useTeammateByIdWithStats(teammateId, startDate, endDate);
  const teammate = dataMate?.datamate;

  useBreadcrumb(getEntityName(state) ?? teammate?.name ?? "Loading...", {
    defaultParents: [{ label: "Datamates", pathname: "/datamates" }],
  });

  const { allIntegrations } = useIntegrations();
  const canEditDatamate = useCanEditPrivacyAware();
  const { enableAdvancedGuardRails } = useTenantInfo();

  const { data: knowledgeSources, isLoading: isKnowledgeSourcesLoading } = useQuery({
    queryKey: ["knowledgeSources"],
    queryFn: () => getKnowledgeBaseSources(),
    enabled: enableAdvancedGuardRails && Boolean(teammate?.knowledge_bases?.length),
  });

  const getChartBar = (action: (typeof GUARDRAIL_ACTIONS)[number], barSize: number) => {
    return (
      <Bar
        key={action.key}
        dataKey={action.label}
        stackId="a"
        fill={action.color}
        barSize={barSize}
        radius={[5, 5, 0, 0]}
      />
    );
  };

  if (isLoading) {
    return <RelativeComponentLoader label="Loading teammate..." />;
  }

  if (!teammate || !dataMate) {
    return <DatamateNotFound />;
  }

  return (
    <MCPProvider>
      <Stack justify="between">
        <div>
          <Stack align="center">
            <BackButton defaultUrl="/datamates" />
            <Typography variant="h3">{teammate.name}</Typography>
          </Stack>
        </div>
        <Stack align="center">
          <Stack align="center">
            <DateRange
              startDate={startDate}
              endDate={endDate}
              onDateRangeSelect={(startDate, endDate) => {
                onUpdate(startDate, endDate);
              }}
            />
          </Stack>
          <UseDatamateWithComponent teammate={teammate} />
          {canEditDatamate(teammate) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" title="More options">
                  <MoreIcon className="al-h-4 al-w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <Button
                  variant="ghost"
                  onClick={() => navigateTo(`/datamate/edit/${teammate.id}`)}
                  className="al-w-full"
                >
                  <Stack className="al-flex-1" justify="start">
                    Edit
                  </Stack>
                </Button>
                {teammate.id && (
                  <DeleteTeammate teammateId={teammate.id} onNavigateAfterDelete>
                    <Button variant="ghost" className="al-w-full">
                      <Stack className="al-flex-1" justify="start">
                        Delete
                      </Stack>
                    </Button>
                  </DeleteTeammate>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </Stack>
      </Stack>

      <Card className="al-mt-6">
        <CardContent className="al-pt-4">
          <Stack gap="md" align={"center"} justify={"between"}>
            <Stack align={"center"}>
              <div>{getDataMateIcon(teammate.name)}</div>
              <Typography variant="section-heading" className="al-my-4">
                {teammate.name}
              </Typography>
            </Stack>
            <Stack>Memory: {teammate.memory_enabled ? "Enabled" : "Disabled"}</Stack>
          </Stack>
          {teammate.user_id && (
            <>
              <PrivacyAndCreatorInfo
                privacy={teammate.privacy || Privacy.PRIVATE}
                userId={teammate.user_id}
              />
            </>
          )}
          <Stack className="al-gap-4" wrap>
            <Stack direction="row" className="al-gap-8">
              <Stack align="center">
                <Typography variant="body" size="xs" className="al-text-muted-foreground">
                  Assists
                </Typography>
                <Typography variant="body">{dataMate.usage.assists}</Typography>
              </Stack>
              <Stack align="center">
                <Typography variant="body" size="xs" className="al-text-muted-foreground">
                  Guardrails
                </Typography>
                <Typography variant="body">{dataMate.usage.guardrails}</Typography>
              </Stack>
              <Stack align="center">
                <Typography variant="body" size="xs" className="al-text-muted-foreground">
                  LLM Costs
                </Typography>
                <Stack direction="row" align="center" className="al-gap-1">
                  <PremiumBanner variant={"small"} />
                </Stack>
              </Stack>
            </Stack>
            <Stack direction="row" className="al-gap-8">
              <Stack align="center">
                <Typography variant="body" size="xs" className="al-text-muted-foreground">
                  Created At
                </Typography>
                <Typography variant="body">
                  {dayjs(dataMate.created_at).format("MMMM D, YYYY h:mm A")}
                </Typography>
              </Stack>
              <Stack align="center">
                <Typography variant="body" size="xs" className="al-text-muted-foreground">
                  Last Used
                </Typography>
                <Typography variant="body">
                  {dataMate.last_used_at
                    ? dayjs(dataMate.last_used_at).format("MMMM D, YYYY h:mm A")
                    : "Never"}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
          <Typography variant="body" className="al-text-muted-foreground al-mt-4 al-text-sm">
            {teammate.description}
          </Typography>
        </CardContent>
      </Card>

      <Card className="al-mt-6">
        <CardHeader>
          <Typography variant="h4">Integrations</Typography>
        </CardHeader>
        <CardContent>
          <Stack wrap>
            {teammate.integrations?.map(i => {
              const integration = allIntegrations.find(
                customIntegration => customIntegration.id === i.id
              );
              if (!integration) return null;
              return (
                <Integration
                  key={integration.id}
                  integration={{
                    ...integration,
                    tools: i.tools.map(t => ({ ...t, name: t.key })),
                  }}
                  toggleIntegration={() => {}}
                  disableToggle={true}
                  isSelected={true}
                />
              );
            })}
          </Stack>
        </CardContent>
      </Card>

      {/* Knowledge Hub Section - Only show if advanced guardrails are enabled and datamate has knowledge bases */}
      {enableAdvancedGuardRails &&
        teammate?.knowledge_bases &&
        teammate.knowledge_bases.length > 0 && (
          <Card className="al-mt-6">
            <CardHeader>
              <Typography variant="h4">Knowledge Hub</Typography>
              <Typography variant="body" className="al-text-muted-foreground al-text-sm">
                Knowledge sources this datamate has access to
              </Typography>
            </CardHeader>
            <CardContent>
              {isKnowledgeSourcesLoading ? (
                <RelativeComponentLoader label="Loading knowledge sources..." />
              ) : (
                <div>
                  {(() => {
                    const validKnowledgeItems = teammate.knowledge_bases
                      .map(itemId => knowledgeSources?.find(item => item.id === itemId))
                      .filter((item): item is NonNullable<typeof item> => Boolean(item));

                    return (
                      <>
                        <Stack direction="row" align="center" className="al-gap-2 al-mb-4">
                          <Badge variant="secondary" className="al-text-xs">
                            {validKnowledgeItems.length} of {knowledgeSources?.length || 0}{" "}
                            knowledge sources
                          </Badge>
                        </Stack>
                        <Stack direction="row" className="al-gap-4 !al-flex-wrap">
                          {validKnowledgeItems.map(knowledgeItem => (
                            <KnowledgeCatalogCard
                              key={knowledgeItem.id}
                              item={knowledgeItem}
                              showCheckbox={false}
                            />
                          ))}
                        </Stack>
                      </>
                    );
                  })()}
                </div>
              )}
            </CardContent>
          </Card>
        )}

      <Card className="al-mt-6">
        <CardHeader>
          <Stack justify="between" direction="row" align="center">
            <Typography variant="h4">Assists</Typography>
            <Stack direction="row" align="center" className="al-gap-2">
              <Typography variant="body">Aggregate by:</Typography>
              <Select value={assistsAggregateBy} onValueChange={setAssistsAggregateBy}>
                <SelectTrigger className="al-w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="integrations">Integrations</SelectItem>
                  {allIntegrations
                    ?.filter(integ =>
                      integ?.id ? teammate.integrations?.map(i => i.id)?.includes(integ.id) : false
                    )
                    .map((integ, index) => (
                      <SelectItem key={index} value={integ.id}>
                        {integ.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </Stack>
          </Stack>
        </CardHeader>
        <CardContent>
          {isAssistAggregationLoading ? (
            <RelativeComponentLoader label="assist aggregations loading..." />
          ) : (
            <Chart
              title="Assists"
              xAxisDataKey={
                assistsAggregateBy === "integrations" ? "integration_type" : "tool_call"
              }
              yAxisLabel={{ value: "Number of Assists" }}
              xAxisLabelFormatter={value => value}
              yAxisLabelFormatter={value => value}
              type={ChartTypes.BarChart}
              data={assistAggregations}
              hideFullscreenButton
              chartProps={{}}
            >
              {({ barSize }) => (
                <Bar barSize={barSize} dataKey="assists" fill="#2563eb" radius={[5, 5, 0, 0]} />
              )}
            </Chart>
          )}
        </CardContent>
      </Card>

      <Card className="al-mt-6">
        <CardHeader>
          <Stack justify="between" direction="row" align="center">
            <Typography variant="h4">Guardrails</Typography>
            <Stack direction="row" align="center" className="al-gap-2">
              <Typography variant="body">Aggregate by:</Typography>
              <Select value={guardrailsAggregateBy} onValueChange={setGuardrailsAggregateBy}>
                <SelectTrigger className="al-w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="integrations">Integrations</SelectItem>
                  {allIntegrations
                    ?.filter(integ =>
                      integ?.id ? teammate.integrations?.map(i => i.id)?.includes(integ.id) : false
                    )
                    .map((integ, index) => (
                      <SelectItem key={index} value={integ.id}>
                        {integ.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </Stack>
          </Stack>
        </CardHeader>
        <CardContent>
          {isGuardRailAggregationLoading ? (
            <RelativeComponentLoader label="loading guardrails aggregations..." />
          ) : (
            <Chart
              title="Guardrails Over Time"
              xAxisDataKey="type"
              yAxisLabel={{ value: "Number of Guardrails" }}
              xAxisLabelFormatter={value => value}
              yAxisLabelFormatter={value => value}
              type={ChartTypes.BarChart}
              data={transformGuardrailAggregations(guardRailAggregations || [])}
              hideFullscreenButton
              chartProps={{}}
            >
              {({ barSize }) => {
                return (
                  <>
                    {GUARDRAIL_ACTIONS.map(action => (
                      <>{getChartBar(action, barSize)}</>
                    ))}
                  </>
                );
              }}
            </Chart>
          )}
        </CardContent>
      </Card>

      {dataMate.events.length > 0 && <DatamateHistory events={dataMate.events} />}
    </MCPProvider>
  );
};

export default CustomTeammate;
